import React, { useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, Clock, IndianRupee, Plus, Receipt } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import Loader from '../../../shared/components/ui/Loader';
import {
  Callout,
  EmptyRow,
  EmptyState,
  Field,
  FilterBar,
  PageBody,
  SectionCard,
  Table,
  TableCard,
  TBody,
  Td,
  Th,
  THead,
  Tile,
  TileRow
} from '../../../shared/components/ui/primitives';
import {
  cancelSiteWalletExpense,
  getMySiteWalletLedgerPage,
  getMySiteWalletProjects,
  getSiteWalletExpensesPage
} from '../../services/api/implementation/impl';
import { useMySiteWallet, useSiteWalletRole } from '../../hooks/useSiteWalletAccess';
import AddExpenseForm from './AddExpenseForm';
import {
  BillLinks,
  LEDGER_TYPE_LABEL,
  ListFooter,
  PAGE_SIZE,
  ReimbursementChip,
  SmallButton,
  StatusChip,
  errorMessage,
  money,
  shortDate,
  tabClass
} from './siteWalletUi';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'expenses', label: 'My expenses' }
];

const dateInput =
  'rounded-control border border-hairline-strong bg-surface-muted px-2 py-1 text-sm text-gray-700 outline-none focus:border-brand-700';

/**
 * My Wallet: the Operations Manager's or Executive's own petty cash. The same
 * holder endpoints the app uses; only their own wallet, and no approve button
 * (approval stays with the Accountant or the builder).
 */
const MyWallet = () => {
  const role = useSiteWalletRole();
  const me = useMySiteWallet(role.mayHoldWallet);
  const [searchParams, setSearchParams] = useSearchParams();
  const active = TABS.find((t) => t.key === searchParams.get('tab')) ?? TABS[0];
  const selectTab = (key) => setSearchParams(key === TABS[0].key ? {} : { tab: key });
  const [adding, setAdding] = useState(false);
  const wallet = me.data;

  return (
    <Layout>
      <Header data={{ name: 'My Wallet' }}>
        {wallet && (
          <div className="flex items-center gap-2" role="tablist" aria-label="My Wallet sections">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={tab.key === active.key}
                onClick={() => selectTab(tab.key)}
                className={tabClass(tab.key === active.key)}
              >
                {tab.label}
              </button>
            ))}
            <button
              type="button"
              disabled={wallet.status !== 'ACTIVE'}
              title={wallet.status !== 'ACTIVE' ? 'This wallet is frozen' : undefined}
              onClick={() => setAdding(true)}
              className="ml-2 flex items-center gap-1 rounded-lg bg-brand-700 px-3 py-1.5 text-sm text-white disabled:opacity-50"
            >
              <Plus size={14} /> Add expense
            </button>
          </div>
        )}
      </Header>
      <PageBody>
        {!role.mayHoldWallet ? (
          <EmptyState title="Site wallets are for Operations Managers and Executives" />
        ) : me.isLoading ? (
          <Loader />
        ) : !wallet ? (
          <EmptyState
            title="You don't have a site wallet"
            hint="The builder opens one when you are to hold petty cash for your sites."
          />
        ) : active.key === 'overview' ? (
          <>
            <Overview wallet={wallet} />
            <MyLedger />
          </>
        ) : (
          <MyExpenses />
        )}
      </PageBody>
      {adding && wallet && (
        <AddExpenseForm
          wallet={wallet}
          onClose={() => setAdding(false)}
          onDone={() => {
            setAdding(false);
            selectTab('expenses');
          }}
        />
      )}
    </Layout>
  );
};

/* ── Overview ──────────────────────────────────────────────────────────── */

const Overview = ({ wallet }) => {
  const frozen = wallet.status !== 'ACTIVE';

  // month_spend is one row per project and category; each card adds up one side.
  const sumBy = (key, label) => {
    const totals = new Map();
    (wallet.month_spend ?? []).forEach((r) => {
      const prev = totals.get(r[key]) ?? { label: r[label] ?? '—', amount: 0 };
      totals.set(r[key], { ...prev, amount: prev.amount + Number(r.amount || 0) });
    });
    return [...totals.values()].sort((a, b) => b.amount - a.amount);
  };
  const byProject = sumBy('project_id', 'project_name');
  const byCategory = sumBy('category_id', 'category_name');

  return (
    <>
      {frozen && (
        <Callout tone="warning">
          This wallet is frozen. You can&apos;t add expenses until the builder unfreezes it.
        </Callout>
      )}
      <TileRow>
        <Tile
          variant="card"
          label="Available"
          hint="What you can spend from the wallet"
          value={money(wallet.available)}
          icon={<IndianRupee size={16} />}
          tone="success"
        />
        <Tile
          variant="card"
          label="On hold"
          hint="Expenses waiting for approval"
          value={money(wallet.on_hold)}
          icon={<Clock size={16} />}
        />
        <Tile
          variant="card"
          label="Owed to me"
          hint="Own-pocket spend to be paid back"
          value={money(wallet.owed_to_holder)}
          icon={<AlertCircle size={16} />}
          tone={Number(wallet.owed_to_holder) > 0 ? 'warning' : 'neutral'}
        />
        <Tile
          variant="card"
          label="Spent this month"
          hint="Approved"
          value={money(wallet.month_spend_total)}
          icon={<Receipt size={16} />}
        />
      </TileRow>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SpendList title="This month by project" rows={byProject} />
        <SpendList title="This month by category" rows={byCategory} />
      </div>
    </>
  );
};

const SpendList = ({ title, rows }) => (
  <SectionCard title={title}>
    {!rows.length ? (
      <p className="text-sm text-gray-500">Nothing approved this month yet.</p>
    ) : (
      <ul className="divide-y divide-gray-100 text-sm">
        {rows.map((r) => (
          <li key={r.label} className="flex justify-between py-1.5">
            <span className="text-gray-800">{r.label}</span>
            <span className="font-medium tabular-nums">{money(r.amount)}</span>
          </li>
        ))}
      </ul>
    )}
  </SectionCard>
);

/* ── Ledger ────────────────────────────────────────────────────────────── */

const MyLedger = () => {
  const [filters, setFilters] = useState({ project_id: '', from: '', to: '' });
  const [page, setPage] = useState(1);
  const projects = useQuery({
    queryKey: ['siteWalletMyProjects'],
    queryFn: getMySiteWalletProjects
  });
  const ledger = useQuery({
    queryKey: ['siteWalletMyLedger', filters, page],
    queryFn: () => getMySiteWalletLedgerPage({ ...filters, page, limit: PAGE_SIZE }),
    placeholderData: keepPreviousData
  });
  const names = useMemo(
    () => new Map((projects.data ?? []).map((p) => [p.id, p.name])),
    [projects.data]
  );
  const rows = ledger.data?.rows ?? [];
  // A new filter starts again from the first page.
  const changeFilters = (next) => {
    setFilters(next);
    setPage(1);
  };
  const set = (key) => (e) => changeFilters({ ...filters, [key]: e.target.value });

  return (
    <>
      <h2 className="pt-2 text-sm font-semibold text-gray-900">Ledger</h2>
      <FilterBar
        end={
          <>
            <Field label="From" size="caption">
              <input
                type="date"
                value={filters.from}
                onChange={set('from')}
                className={dateInput}
              />
            </Field>
            <Field label="To" size="caption">
              <input type="date" value={filters.to} onChange={set('to')} className={dateInput} />
            </Field>
            {(filters.from || filters.to || filters.project_id) && (
              <button
                type="button"
                onClick={() => changeFilters({ project_id: '', from: '', to: '' })}
                className="mb-1.5 text-sm font-semibold text-brand-700 hover:underline"
              >
                Clear
              </button>
            )}
          </>
        }
      >
        <Field label="Project" size="caption">
          <select value={filters.project_id} onChange={set('project_id')} className={dateInput}>
            <option value="">All projects</option>
            {(projects.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
      </FilterBar>
      {ledger.isLoading ? (
        <Loader />
      ) : (
        <>
          <TableCard>
            <Table>
              <THead>
                <tr>
                  <Th>When</Th>
                  <Th>Type</Th>
                  <Th>Project</Th>
                  <Th>Reason</Th>
                  <Th align="right">Amount</Th>
                </tr>
              </THead>
              <TBody>
                {!rows.length && <EmptyRow colSpan={5}>No entries.</EmptyRow>}
                {rows.map((l) => (
                  <tr key={l.id}>
                    <Td className="whitespace-nowrap text-gray-600">{shortDate(l.created_at)}</Td>
                    <Td>{LEDGER_TYPE_LABEL[l.type] ?? l.type}</Td>
                    <Td className="text-gray-600">
                      {l.project_id ? (names.get(l.project_id) ?? `#${l.project_id}`) : '—'}
                    </Td>
                    <Td className="max-w-xs text-sm text-gray-600">{l.reason || '—'}</Td>
                    <Td
                      align="right"
                      numeric
                      className={`font-medium ${Number(l.amount) < 0 ? 'text-danger-fg' : 'text-success-fg'}`}
                    >
                      {Number(l.amount) > 0 ? '+' : ''}
                      {money(l.amount)}
                    </Td>
                  </tr>
                ))}
              </TBody>
            </Table>
          </TableCard>
          <ListFooter
            meta={ledger.data?.meta}
            page={page}
            onPageChange={setPage}
            isFetching={ledger.isFetching}
          />
        </>
      )}
    </>
  );
};

/* ── My expenses ───────────────────────────────────────────────────────── */

const MyExpenses = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const expenses = useQuery({
    queryKey: ['siteWalletMyExpenses', page],
    queryFn: () => getSiteWalletExpensesPage({ mine: 1, page, limit: PAGE_SIZE }),
    placeholderData: keepPreviousData
  });
  const rows = expenses.data?.rows ?? [];

  const cancel = useMutation({
    mutationFn: cancelSiteWalletExpense,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['siteWalletMyExpenses'] });
      queryClient.invalidateQueries({ queryKey: ['siteWalletMe'] });
      toast.success('Expense cancelled');
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not cancel the expense'))
  });

  if (expenses.isLoading) return <Loader />;

  return (
    <>
      <TableCard>
        <Table>
          <THead>
            <tr>
              <Th>Spent on</Th>
              <Th>Project · Category</Th>
              <Th align="right">Amount</Th>
              <Th>Bill</Th>
              <Th>Status</Th>
              <Th>Note</Th>
              <Th />
            </tr>
          </THead>
          <TBody>
            {!rows.length && (
              <EmptyRow colSpan={7}>You haven&apos;t added any expenses yet.</EmptyRow>
            )}
            {rows.map((e) => (
              <tr key={e.id}>
                <Td className="whitespace-nowrap">{shortDate(e.spent_on)}</Td>
                <Td>
                  <div className="text-gray-900">{e.project_name ?? '—'}</div>
                  <div className="text-sm text-gray-500">{e.category_name ?? '—'}</div>
                </Td>
                <Td align="right" numeric>
                  <div className="font-semibold">{money(e.amount)}</div>
                  {Number(e.own_amount) > 0 && (
                    <div className="text-sm text-gray-500">
                      {money(e.wallet_amount)} wallet · {money(e.own_amount)} own pocket
                    </div>
                  )}
                </Td>
                <Td>
                  <BillLinks urls={e.bill_urls} />
                </Td>
                <Td>
                  <StatusChip status={e.status} />
                  {e.status === 'SUBMITTED' && (
                    <div className="mt-1 text-sm text-gray-500">
                      Waiting on{' '}
                      {e.approver_role === 'ACCOUNTS'
                        ? (e.approver_name ?? 'Accountant')
                        : 'Builder'}
                    </div>
                  )}
                  {e.status === 'REJECTED' && e.reject_reason && (
                    <div className="mt-1 text-sm text-danger-fg">{e.reject_reason}</div>
                  )}
                  {e.status === 'REVERSED' && e.reverse_reason && (
                    <div className="mt-1 text-sm text-danger-fg">{e.reverse_reason}</div>
                  )}
                  <ReimbursementChip expense={e} />
                </Td>
                <Td className="max-w-xs text-sm text-gray-700">{e.note || '—'}</Td>
                <Td>
                  {e.status === 'SUBMITTED' && (
                    <SmallButton
                      tone="danger"
                      disabled={cancel.isPending}
                      onClick={() => cancel.mutate(e.id)}
                    >
                      Cancel
                    </SmallButton>
                  )}
                </Td>
              </tr>
            ))}
          </TBody>
        </Table>
      </TableCard>
      <ListFooter
        meta={expenses.data?.meta}
        page={page}
        onPageChange={setPage}
        isFetching={expenses.isFetching}
      />
    </>
  );
};

export default MyWallet;
